"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { cn } from "@/lib/utils";
import Combobox, { type ComboboxOption } from "@/components/frontend/ui/form/inputs/combobox";
import Input from "@/components/frontend/ui/form/inputs/input";
import { DEFAULT_BLOG_SORT, type SortValue } from "./types";

type SortOption = {
	value: SortValue;
	label: string;
};

interface BlogOverviewFiltersProps {
	basePath: string;
	showSearchBar: boolean;
	showSort: boolean;
	initialQuery: string;
	initialSort: SortValue;
	onFilterChange?: (query: string, sort: SortValue) => void;
	searchPlaceholder: string;
	formVariantId: string;
	sortOptions: SortOption[];
}

function buildListingHref(basePath: string, q: string, sort: SortValue): string {
	const params = new URLSearchParams();
	if (q.trim().length > 0) params.set("q", q.trim());
	if (sort !== DEFAULT_BLOG_SORT) params.set("sort", sort);
	const query = params.toString();
	return query.length > 0 ? `${basePath}?${query}` : basePath;
}

export function BlogOverviewFilters({
	basePath,
	showSearchBar,
	showSort,
	initialQuery,
	initialSort,
	onFilterChange,
	searchPlaceholder,
	formVariantId,
	sortOptions,
}: BlogOverviewFiltersProps) {
	const router = useRouter();
	const [query, setQuery] = useState(initialQuery);
	const [sort, setSort] = useState<SortValue>(initialSort);
	const initialRenderRef = useRef(true);
	const hasInteractedRef = useRef(false);
	const sortComboboxValue = useMemo<ComboboxOption | undefined>(() => sortOptions.find((option) => option.value === sort), [sort, sortOptions]);

	useEffect(() => {
		setQuery(initialQuery);
		setSort(initialSort);
		hasInteractedRef.current = false;
	}, [initialQuery, initialSort]);

	const targetHref = useMemo(() => buildListingHref(basePath, query, sort), [basePath, query, sort]);

	useEffect(() => {
		if (initialRenderRef.current) {
			initialRenderRef.current = false;
			return;
		}

		if (!hasInteractedRef.current) return;

		const timeout = window.setTimeout(() => {
			onFilterChange?.(query, sort);
			router.push(targetHref, { scroll: false });
		}, 250);

		return () => window.clearTimeout(timeout);
	}, [onFilterChange, query, router, sort, targetHref]);

	if (!showSearchBar && !showSort) return null;

	return (
		<div className={cn(`ms-form-${formVariantId}`, "mb-4 grid gap-3 md:grid-cols-[minmax(0,1fr)_16rem]")}>
			{showSearchBar && (
				<Input
					type="text"
					name="q"
					value={query}
					onChange={(event) => {
						hasInteractedRef.current = true;
						setQuery(event.target.value);
					}}
					placeholder={searchPlaceholder}
				/>
			)}
			{showSort && (
				<Combobox
					name="sort"
					value={sortComboboxValue}
					clearable={false}
					onValueChange={(value) => {
						hasInteractedRef.current = true;
						setSort((value?.value as SortValue | undefined) ?? DEFAULT_BLOG_SORT);
					}}
					options={sortOptions}
				/>
			)}
		</div>
	);
}
